/**
 * =============================================================================
 * SCRIPT-NAME:   uje_UserJourneyExplorer.js
 * MODUL:         User-Journey & Funnel Explorer (UJE)
 * BESCHREIBUNG:  Frontend-Controller. Wiederverwendbar: standalone (App.js) ODER
 *                eingebettet im Ads-Dashboard (embedded).
 * FEATURE v4.0:  - embedded-Modus (Props statt interner Filter): campaigns/von/bis
 *                - Ein-Kampagnen-Picker aus den übergebenen Kampagnen
 *                - Conversion: farbige Stufen-Badge (uje_get_visitors.conversion)
 *                  + Milestone-Icons kombiniert
 *                Backend geteilt via api/journey_common.php.
 *
 * VERSION:       v4.0 (Merge-fähig)
 * DATUM:         2026-07-06
 * AUTHOR:        KI-Core
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import BackButton from "./BackButton";
import "./uje_UserJourneyExplorer.css";

// Conversion-Stufe -> Badge-Farbklasse (identisch zu MiniJourney)
const CONVERSION_CLS = {
    'Coaching':   'conv-coaching',
    'SG geführt': 'conv-sg',
    'SG gebucht': 'conv-sg',
    'EG geführt': 'conv-eg',
    'EG gebucht': 'conv-eg',
    'Webinar':    'conv-webinar',
    '—':          'conv-none',
};

/**
 * @param {boolean} embedded   kompakter Modus (im Ads-Dashboard). Ohne Seiten-Rahmen/BackButton.
 * @param {Array}   campaigns  [{campaign_id, campaign_name}] der AUSGEWÄHLTEN Kampagnen (Picker-Quelle).
 * @param {string}  von,bis    Datumsbereich vom Parent (embedded).
 */
export default function UserJourneyExplorer({ embedded = false, campaignNames = null, term = null, von = null, bis = null }) {
    const today = new Date().toISOString().split('T')[0];

    // --- ZEITRAUM ---
    const [startDate, setStartDate] = useState(von || today);
    const [endDate, setEndDate]     = useState(bis || today);

    // --- FILTER ---
    const [campaignList, setCampaignList]       = useState([]);
    const [termList, setTermList]               = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState("");
    const [selectedTerm, setSelectedTerm]       = useState("");

    // --- DATEN ---
    const [visitors, setVisitors]         = useState([]);
    const [selectedPath, setSelectedPath] = useState(null);
    const [sectionStats, setSectionStats] = useState([]);
    const [funnelLp, setFunnelLp]         = useState("");   // LP-Filter der Sektions-Performance (clientseitig)
    const [loading, setLoading]           = useState(false);

    const token = localStorage.getItem("token");

    // embedded: Datum aus Props übernehmen
    useEffect(() => { if (embedded && von) setStartDate(von); }, [embedded, von]);
    useEffect(() => { if (embedded && bis) setEndDate(bis); }, [embedded, bis]);

    // (embedded-Steuerung: siehe Effect nach loadAllData — obere Auswahl steuert direkt)

    // Hilfsfunktion: Filter laden (Keywords je Kampagne; Kampagnen nur standalone)
    const fetchFilters = useCallback(async (mode, campaignParam = '') => {
        try {
            const url = `https://freiseinapp.de/api/uje_get_filters.php?mode=${mode}&campaign=${encodeURIComponent(campaignParam)}&startDate=${startDate}&endDate=${endDate}`;
            const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            const json = await res.json();
            if (json.success) {
                if (mode === 'campaigns') setCampaignList(json.data || []);
                if (mode === 'terms') setTermList(json.data || []);
            }
        } catch (e) { console.error("Filter-Ladefehler:", e); }
    }, [startDate, endDate, token]);

    // Kaskade: Kampagne -> Keywords (nur standalone; embedded bekommt das Keyword von oben)
    useEffect(() => {
        if (embedded) return;
        if (selectedCampaign) { fetchFilters('terms', selectedCampaign); setSelectedTerm(""); }
        else { setTermList([]); setSelectedTerm(""); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaign]);

    // HAUPTFUNKTION: Besucher + Statistik laden
    const loadAllData = useCallback(async (campOverride = null, termOverride = null) => {
        const camp = campOverride !== null ? campOverride : selectedCampaign;
        const trm  = termOverride !== null ? termOverride : selectedTerm;
        setLoading(true);
        setSelectedPath(null);
        try {
            const queryParams = `startDate=${startDate}&endDate=${endDate}&campaign=${encodeURIComponent(camp)}&term=${encodeURIComponent(trm)}`;

            const listRes = await fetch(`https://freiseinapp.de/api/uje_get_visitors.php?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const listData = await listRes.json();
            if (listData.success) setVisitors(listData.visitorListData || []);

            const statsRes = await fetch(`https://freiseinapp.de/api/uje_get_stats.php?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const statsData = await statsRes.json();
            if (statsData.success) setSectionStats(statsData.stats || []);

            if (!embedded) await fetchFilters('campaigns'); // embedded bekommt Kampagnen per Props
        } catch (e) { console.error("Haupt-Ladefehler:", e); }
        setLoading(false);
    }, [startDate, endDate, selectedCampaign, selectedTerm, token, embedded, fetchFilters]);

    // CSV-Export (wiederverwendbar): lädt uje_export_csv.php mit dem AKTUELLEN Filter
    // (selectedCampaign/-Term werden in beiden Modi gesetzt) und stößt den Download an.
    // grain='sections' → Sektions-Aggregat (Funnel-Kurve) · 'visitors' → Besucher-Ebene.
    const downloadCsv = async (grain) => {
        const qs = `grain=${grain}&startDate=${startDate}&endDate=${endDate}&campaign=${encodeURIComponent(selectedCampaign)}&term=${encodeURIComponent(selectedTerm)}`;
        try {
            const res = await fetch(`https://freiseinapp.de/api/uje_export_csv.php?${qs}`, { headers: { "Authorization": `Bearer ${token}` } });
            if (!res.ok) { console.error("CSV-Export HTTP", res.status); return; }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `uje_${grain}_${startDate}_${endDate}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) { console.error("CSV-Export-Fehler:", e); }
    };

    // embedded: die obere Auswahl (Kampagnen-Checkboxen + Keyword) steuert die Journey direkt.
    // campKey = Wert-Schlüssel (String), damit der Effect NICHT bei jeder neuen Array-Referenz feuert.
    const campKey = Array.isArray(campaignNames) ? campaignNames.join(',') : (campaignNames || '');
    useEffect(() => {
        if (!embedded) return;
        const trm = term || '';
        setSelectedCampaign(campKey);
        setSelectedTerm(trm);
        if (campKey) loadAllData(campKey, trm);
        else { setVisitors([]); setSectionStats([]); setSelectedPath(null); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, campKey, term, startDate, endDate]);

    // Einzelnen Pfad laden
    const loadPath = async (visitorId) => {
        setLoading(true);
        try {
            const url = `https://freiseinapp.de/api/uje_get_path.php?visitorId=${encodeURIComponent(visitorId)}&startDate=${startDate}&endDate=${endDate}`;
            const response = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            const data = await response.json();
            if (data.success) setSelectedPath(data);
        } catch (e) { console.error("Pfad-Ladefehler:", e); }
        setLoading(false);
    };

    // Datum + Uhrzeit deutsch für die Besucherliste: "06.07.2026 03:28 Uhr"
    const fmtDateTime = (ts) => {
        if (!ts) return '';
        const [d, t] = ts.split(' ');
        const [y, m, day] = (d || '').split('-');
        return `${day}.${m}.${y} ${(t || '').substring(0, 5)} Uhr`;
    };

    // Geräte-Verteilung je Sektion (SectionViews pro Gerät): 📱 Smartphone · 💻 Desktop · 📲 Tablet
    const renderDevices = (s) => {
        const p = [];
        if (Number(s.dev_smartphone) > 0) p.push(`📱 ${s.dev_smartphone}`);
        if (Number(s.dev_computer)   > 0) p.push(`💻 ${s.dev_computer}`);
        if (Number(s.dev_tablet)     > 0) p.push(`📲 ${s.dev_tablet}`);
        return p.length ? p.join(' · ') : '—';
    };

    // Conversion-Zelle: farbige Stufen-Badge + Milestone-Icons (Georg 2026-07-06)
    const renderConversion = (v) => {
        const conv = v.conversion || '—';
        const m = v.milestones || {};
        return (
            <div className="uj-conv-cell">
                <span className={`mj-conv-badge ${CONVERSION_CLS[conv] || 'conv-none'}`}>{conv}</span>
                <span className="uj-milestones">
                    <span title="Video gestartet" style={{ opacity: m.webinar_booked ? 1 : 0.15 }}>📽️</span>
                    <span title="Video fertig" style={{ opacity: m.webinar_engaged ? 1 : 0.15 }}>✅</span>
                    <span title="Kontakt / EG" style={{ opacity: m.eg_booked ? 1 : 0.15 }}>📞</span>
                </span>
            </div>
        );
    };

    // Verweildauer für den individuellen Pfad
    let totalDuration = "0m 0s";
    if (selectedPath && selectedPath.path && selectedPath.path.length > 0) {
        const firstEvent = new Date(selectedPath.path[0].event_timestamp.replace(' ', 'T'));
        const lastEvent  = new Date(selectedPath.path[selectedPath.path.length - 1].event_timestamp.replace(' ', 'T'));
        const diffMs   = lastEvent - firstEvent;
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor((diffMs % 60000) / 1000);
        totalDuration = `${diffMins}m ${diffSecs}s`;
    }

    // Kampagne/Keyword-Auswahl (in beiden Modi)
    const filterControls = (
        <>
            <div className="filter-group" style={{ minWidth: '200px' }}>
                <label>Kampagne</label>
                <select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)} className="funnel-lp-select">
                    {!embedded && <option value="">-- Alle --</option>}
                    {campaignList.map((cam, idx) => <option key={idx} value={cam}>{cam}</option>)}
                </select>
            </div>
            <div className="filter-group" style={{ minWidth: '200px' }}>
                <label>Keyword</label>
                <select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)} className="funnel-lp-select" disabled={!selectedCampaign} style={{ opacity: selectedCampaign ? 1 : 0.5 }}>
                    <option value="">-- Alle --</option>
                    {termList.map((t, idx) => <option key={idx} value={t}>{t}</option>)}
                </select>
            </div>
            {embedded && (
                <button onClick={loadAllData} className="analyze-button" disabled={loading}>
                    {loading ? "Lädt…" : "🔄 Aktualisieren"}
                </button>
            )}
        </>
    );

    // LP-Filter der Sektions-Performance (clientseitig — die Zeilen sind schon pro LP da)
    const funnelLps = Array.from(new Set(sectionStats.map(s => s.lp_id).filter(Boolean)));
    const activeFunnelLp = funnelLps.includes(funnelLp) ? funnelLp : "";
    const shownStats = sectionStats.filter(s => !activeFunnelLp || s.lp_id === activeFunnelLp);

    // Besucherliste + individueller Pfad + Sektions-Performance (in beiden Modi)
    const gridAndFunnel = (
        <>
            <div className="explorer-grid">
                {/* Links: Besucher */}
                <div className="results-section">
                    <h3>Besucher ({visitors.length})</h3>
                    <div className="table-wrapper">
                        <table className="analytics-table">
                            <thead><tr><th>Identität / Zeit</th><th>Conversion</th><th>Dauer</th></tr></thead>
                            <tbody>
                                {visitors.map(v => (
                                    <tr key={v.visitor_id} onClick={() => loadPath(v.visitor_id)} className={`clickable-row ${selectedPath?.visitorId === v.visitor_id ? 'active-row' : ''}`}>
                                        <td>
                                            <div className="visitor-info" style={{ display: 'flex', alignItems: 'center' }}>
                                                <span style={{ fontSize: '1.5rem', marginRight: '10px' }} title="Gerätetyp">{v.device_icon || '❓'}</span>
                                                <div>
                                                    <strong>{v.vorname ? `${v.vorname} ${v.nachname || ''}` : "Anonym"}</strong>
                                                    <br/>
                                                    <small>{fmtDateTime(v.first_seen)}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{renderConversion(v)}</td>
                                        <td><small>{v.session_duration_formatted}</small></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Rechts: Pfad */}
                <div className="results-section">
                    <h3>Individueller Pfad</h3>
                    {selectedPath ? (
                        <div className="timeline">
                            <div className="identity-header-box" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px', borderLeft: '6px solid #28a745' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <div>
                                        <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>USER</small><br/>
                                        <strong>{selectedPath.meta.vorname || "Unbekannt"} {selectedPath.meta.nachname || ""}</strong>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>VERWEILDAUER</small><br/>
                                        <span style={{ color: '#17a2b8', fontWeight: 'bold' }}>{totalDuration}</span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>KEYWORD</small><br/>
                                        <span style={{ color: '#d63384', fontWeight: 'bold' }}>{selectedPath.meta.utm_term || '-'}</span>
                                    </div>
                                </div>
                                <div style={{ marginBottom: '10px', borderTop: '1px solid #e9ecef', paddingTop: '5px' }}>
                                    <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>SYSTEM-INFO (User Agent)</small><br/>
                                    <div style={{ fontSize: '0.75rem', color: '#555', fontFamily: 'Consolas, monospace', wordBreak: 'break-all', backgroundColor: '#fff', padding: '5px', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                                        {selectedPath.meta.user_agent || "Keine Info verfügbar"}
                                    </div>
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#adb5bd', fontFamily: 'monospace', textAlign: 'right' }}>
                                    ID: {selectedPath.visitorId}
                                </div>
                            </div>

                            <ul className="path-list">
                                {selectedPath.path.map((step, i) => {
                                    let detailText = "Page Load";
                                    let icon = "⚪";
                                    if (step.event_type === 'SectionView') { detailText = `Sektion: ${step.section_name}`; icon = "👁️"; }
                                    else if (step.event_type === 'CtaClick') { detailText = `Klick: ${step.cta_text}`; icon = "🖱️"; }
                                    else if (step.event_type.startsWith('Video')) {
                                        const title = step.video_title || "Video";
                                        if (step.event_type === 'VideoStart') { detailText = `Start: ${title}`; icon = "▶️"; }
                                        else if (step.event_type === 'VideoComplete') { detailText = `Fertig: ${title}`; icon = "✅"; }
                                        else if (step.event_type === 'VideoProgress') { detailText = `${title} (${step.video_progress}%)`; icon = "📺"; }
                                    } else if (step.event_type === 'PageLeaveEnd') { detailText = "Seite verlassen"; icon = "🚪"; }
                                    return (
                                        <li key={i} className={`path-item event-${step.event_type}`}>
                                            <span className="path-time">{step.event_timestamp?.split(' ')[1]?.substring(0, 8)}</span>
                                            <span className="path-icon" style={{ marginRight: '10px', fontSize: '1.2rem' }}>{icon}</span>
                                            <span className="path-event-type">{step.event_type}</span>
                                            <span className="path-detail">{detailText}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ) : <div className="placeholder-text">Wähle einen Besucher.</div>}
                </div>
            </div>

            {/* Sektions-Performance (das LP-Instrument) */}
            <div className="stats-summary-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>📊 Sektions-Performance{selectedCampaign ? ` — ${selectedCampaign.split(',').length > 1 ? selectedCampaign.split(',').length + ' Kampagnen' : selectedCampaign}` : ' (Global)'}</h3>
                    {funnelLps.length > 1 && (
                        <select value={activeFunnelLp} onChange={e => setFunnelLp(e.target.value)}
                                style={{ padding: '3px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                title="Funnel auf eine LP filtern">
                            <option value="">Alle LPs ({funnelLps.length})</option>
                            {funnelLps.map((lp, i) => <option key={i} value={lp}>{lp}</option>)}
                        </select>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button onClick={() => downloadCsv('sections')} disabled={!selectedCampaign && sectionStats.length === 0}
                                title="Sektions-Aggregat als CSV (1 Zeile je LP×Sektion: Views/Besucher/Geräte/Ø-Dauer/Abbruch/CTA/Retention). Für Excel/Claude Desktop."
                                style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #1a7f5a', background: '#eafaf3', color: '#1a7f5a', cursor: 'pointer', fontSize: '13px' }}>
                            ⬇ Sektionen-CSV
                        </button>
                        <button onClick={() => downloadCsv('visitors')} disabled={!selectedCampaign && sectionStats.length === 0}
                                title="Besucher-Ebene als CSV (1 Zeile pro Besucherin: Gerät/Keyword/Ad/Verweildauer/tiefste Sektion/CTA/Video/Conversion). Max. Analyse-Tiefe — ohne Namen/E-Mail."
                                style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #1a5a7f', background: '#eaf3fa', color: '#1a5a7f', cursor: 'pointer', fontSize: '13px' }}>
                            ⬇ Besucher-CSV
                        </button>
                    </div>
                </div>
                {sectionStats.length > 0 ? (
                    <div className="table-wrapper">
                        <table className="analytics-table stats-table">
                            <thead><tr><th>LP</th><th>Sektion</th><th title="SectionView-Events, NICHT distinct Besucher — eine Besucherin kann eine Sektion mehrfach sehen (Hoch-/Runter-Scrollen).">Views (Events)</th><th title="Distinct Besucher (visitor_id) je Sektion — jede Besucherin zählt genau 1×, egal wie oft sie scrollt.">Besucher</th><th title="Distinct Besucher je Gerät (nicht Events).">Geräte</th><th>Ø Dauer</th><th>Abbruch</th><th>Status</th></tr></thead>
                            <tbody>
                                {shownStats.map((stat, index) => (
                                    <tr key={index}>
                                        <td><strong>{stat.lp_id || '—'}</strong></td>
                                        <td>{stat.section}</td>
                                        <td>{stat.total_views}</td>
                                        <td><strong>{stat.dist_visitors}</strong></td>
                                        <td>{renderDevices(stat)}</td>
                                        <td>{stat.avg_duration_sec}s</td>
                                        <td style={{ color: stat.drop_off_rate > 35 ? '#dc3545' : '#28a745' }}>{stat.drop_off_rate}%</td>
                                        <td>{stat.drop_off_rate > 35 ? '⚠️ Flaschenhals' : '✅ OK'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : <div className="placeholder-text">{embedded ? "Keine Sektions-Daten für diese Kampagne/Zeitraum." : 'Drücke auf "Analysieren" um Daten zu laden.'}</div>}
            </div>
        </>
    );

    // --- EMBEDDED: kompakt, KEIN interner Picker (obere Auswahl steuert) ---
    if (embedded) {
        return <div className="uj-embedded">{gridAndFunnel}</div>;
    }

    // --- STANDALONE: volle Seite ---
    return (
        <div className="analytics-page-wrapper">
            <div className="analytics-dashboard-container">
                <header className="analytics-header">
                    <h2>👣 User-Journey & Funnel Explorer</h2>
                </header>

                <div className="date-filter-section" style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="filter-group">
                        <label>Von</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="date-input" />
                    </div>
                    <div className="filter-group">
                        <label>Bis</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="date-input" />
                    </div>
                    <button onClick={loadAllData} className="analyze-button" disabled={loading}>
                        {loading ? "Lade..." : "🔍 Daten analysieren"}
                    </button>
                    {filterControls}
                </div>

                {gridAndFunnel}
                <BackButton to="/admin" />
            </div>
        </div>
    );
}
